import React, { useRef, useState, useEffect, useCallback } from "react";
import { Upload } from "lucide-react";

/**
 * Universal drag-and-drop + paste file zone.
 * - Click to browse files
 * - Drop files
 * - Ctrl+V / Cmd+V paste (image from clipboard, screenshot)
 *
 * Props:
 *   accept   : string MIME list ("image/*,application/pdf")
 *   onFile   : (File) => void           single-file callback (called per file when multiple=true)
 *   multiple : bool                      allow many
 *   disabled : bool
 *   label    : string                    label inside the box
 *   compact  : bool                      smaller layout
 *   children : alternative content
 *   testId   : string                    data-testid
 *   pasteScope : "global" | "self"      where to listen for paste (default "self")
 */
export default function DropZone({
    accept,
    onFile,
    multiple = false,
    disabled = false,
    label,
    compact = false,
    children,
    testId,
    pasteScope = "self",
    className = "",
}) {
    const inputRef = useRef(null);
    const zoneRef = useRef(null);
    const [over, setOver] = useState(false);

    const handleFiles = useCallback((files) => {
        if (disabled) return;
        if (!files || files.length === 0) return;
        const arr = Array.from(files);
        const toSend = multiple ? arr : [arr[0]];
        toSend.forEach((f) => onFile?.(f));
    }, [disabled, multiple, onFile]);

    // --- Paste handler ---
    useEffect(() => {
        if (disabled) return;
        const handler = (e) => {
            // For pasteScope=self, only react if the focus is within our zone or its descendants
            if (pasteScope === "self") {
                const active = document.activeElement;
                if (!zoneRef.current || !(zoneRef.current === active || zoneRef.current.contains(active))) return;
            }
            const items = e.clipboardData?.items;
            if (!items) return;
            const files = [];
            for (const it of items) {
                if (it.kind === "file") {
                    const f = it.getAsFile();
                    if (f) files.push(f);
                }
            }
            if (files.length) {
                e.preventDefault();
                handleFiles(files);
            }
        };
        window.addEventListener("paste", handler);
        return () => window.removeEventListener("paste", handler);
    }, [handleFiles, disabled, pasteScope]);

    const onDragOver = (e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
    };
    const onDragLeave = () => setOver(false);
    const onDrop = (e) => {
        e.preventDefault();
        setOver(false);
        handleFiles(e.dataTransfer?.files);
    };

    return (
        <div
            ref={zoneRef}
            tabIndex={0}
            role="button"
            aria-disabled={disabled}
            onClick={() => !disabled && inputRef.current?.click()}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            data-testid={testId}
            className={`relative outline-none rounded-md border-2 border-dashed transition cursor-pointer focus:ring-2 focus:ring-brand-300 ${
                over ? "border-brand-500 bg-brand-50" : "border-neutral-200 hover:border-brand-300 hover:bg-neutral-50"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${compact ? "px-3 py-2" : "px-4 py-6"} ${className}`}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                className="hidden"
                data-testid={testId ? `${testId}-input` : undefined}
            />
            {children || (
                <div className="flex flex-col items-center justify-center text-center gap-1">
                    <Upload className={`${compact ? "w-3.5 h-3.5" : "w-5 h-5"} text-neutral-400`} />
                    <p className={`${compact ? "text-[11px]" : "text-xs"} text-neutral-500 leading-snug`}>{label}</p>
                </div>
            )}
        </div>
    );
}
